import * as constants from "@/config/constants";
import {
	customBehaviorByMobileTargetedPath,
	customBehaviorByPathsWithFilter,
} from "@/config/customBehaviorByPath";
import * as irConstants from "@/config/site/insuranceranked";
import { move, path, pathOr, pipe } from "ramda";

import {
	filterAdNetworkCompanies,
	filterEligibleAndGeoRelevantCompanies,
} from "./filters";
import { logError } from "./logError";

export const replacePhoneNumbers = () => {
	if (window.Invoca) {
		window.Invoca.PNAPI.run();
	}
};

export const getFilterPlaceholder = (vertical, filter, filteringKey) => {
	const placeholder = path(
		[vertical, "filteringInformation", "placeholders", filter, filteringKey],
		constants.SITE_INFORMATION,
	);
	const defaultPlaceholder = path(
		[vertical, "filteringInformation", "placeholders", "DEFAULT", filteringKey],
		constants.SITE_INFORMATION,
	);
	if (!defaultPlaceholder)
		throw new Error(
			`defaultPlaceholder is undefined for vertical: ${vertical} and filteringKey: ${filteringKey} `,
		);
	return placeholder === undefined ? defaultPlaceholder : placeholder;
};

export const getMobileFilteringLabel = (vertical, title, value) => {
	const mobileFilteringButtonSelect = path(
		[vertical, "filteringInformation", "mobileFilteringButtonSelects"],
		constants.SITE_INFORMATION,
	).filter((filter) => filter.title === title);
	const targetedOption = mobileFilteringButtonSelect[0].options.filter(
		(opt) => opt.value.toString() === value,
	);
	return targetedOption[0].label;
};

export const resolveRatingKey = (
	vertical,
	urlPathFilter,
	filteringInformation,
	isMobileTargetedPath,
) => {
	const isNotUserFiltered =
		filteringInformation[constants.IS_NOT_USER_FILTERED];
	const isNotModalFiltered =
		filteringInformation[constants.IS_NOT_MODAL_FILTERED];

	/* boolean for determining if a vertical uses mobileRating */
	const isMobileRatingUsedForMobileTargetedPaths = pathOr(
		false,
		[vertical, "isMobileRatingUsedForMobileTargetedPaths"],
		constants.SITE_INFORMATION,
	);

	/* If a vertical implements sorting by mobileRating, browsing a mobile targeted path, and no filters have been applied*/
	if (
		isMobileRatingUsedForMobileTargetedPaths &&
		isMobileTargetedPath &&
		isNotUserFiltered &&
		isNotModalFiltered
	) {
		return "mobileRating";
	} else {
		// Default key for finding the rating of a company by urlPathFilter. Defaults to rating if it's not found
		const defaultOrUrlPathFilterRatingKey = pathOr(
			"rating",
			[constants.SITE, vertical, urlPathFilter, constants.CUSTOM_RATING_KEY],
			customBehaviorByPathsWithFilter,
		);

		const creditQuality = filteringInformation.creditQuality;
		const mortgageType = filteringInformation.mortgageType;
		const isAged = pathOr(
			false,
			[irConstants.AGED_FILTERING_KEY],
			filteringInformation,
		);
		const olderThan50FilteringKey = isAged
			? irConstants.AGED_FILTERING_KEY
			: undefined;
		const keyPath = creditQuality || olderThan50FilteringKey || mortgageType;

		return pathOr(
			defaultOrUrlPathFilterRatingKey,
			[
				constants.SITE,
				vertical,
				constants.CUSTOM_RATING_KEY_BY_UI_FILTERING,
				keyPath,
			],
			customBehaviorByPathsWithFilter,
		);
	}
};

const sortOrganic = (companies, ratingKey) =>
	companies.sort(
		(a, b) => path(["fields", ratingKey], b) - path(["fields", ratingKey], a),
	);

const applyPositionOverride = (companies, overrideCompanySlug) => {
	if (!overrideCompanySlug) {
		return companies;
	}
	const originalPosition = companies.findIndex(
		(company) => company.fields.companySlug === overrideCompanySlug,
	);
	if (originalPosition !== -1) {
		return move(originalPosition, 0, companies);
	}

	return companies;
};

const sortMediaAlphaCompanies = (companies, ratingKey) => {
	const mediaAlphaCompanies = companies.filter(
		(company) => company.fields.isMediaAlpha || company.fields.is_direct,
	); // sort direct partners in MA payload as if they're MA. Allows sorting by bid
	const organicCompanies = companies.filter(
		(company) => !company.fields.isMediaAlpha && !company.fields.is_direct,
	);
	const sortedMediaAlphaCompanies = mediaAlphaCompanies.sort(
		(a, b) => path(["fields", "bid"], b) - path(["fields", "bid"], a),
	);
	return [
		...sortedMediaAlphaCompanies,
		...sortOrganic(organicCompanies, ratingKey),
	];
};

/**
 * Main company sort function used when displaying company list
 * @returns
 */
export const sortCompanies = (
	vertical,
	filter,
	filteringInformation,
	companies,
	isMobileTargetedPath,
	sortCompaniesByMediaAlphaBid,
	companyPositionOverride,
) => {
	const ratingKey = resolveRatingKey(
		vertical,
		filter,
		filteringInformation,
		isMobileTargetedPath,
	);
	if (sortCompaniesByMediaAlphaBid) {
		return sortMediaAlphaCompanies(companies, ratingKey);
	}

	const organicallySortedCompanies = [...sortOrganic(companies, ratingKey)];
	const positionOverrideCompanies = applyPositionOverride(
		organicallySortedCompanies,
		companyPositionOverride,
	);

	// exclude any companies with rating of 0
	// useful in verticals where we have per-path rankings (ex. life insurance over 50 toggle)
	const companiesWithValidRating = positionOverrideCompanies.filter(
		(company) => path(["fields", ratingKey], company) > 0,
	);

	return companiesWithValidRating;
};

export const shouldFilteringModalRender = (
	isForceRendered,
	isTargetedPath,
	hasModalBeenClosed,
	isModalUrlParamMatched,
	vertical,
) => {
	if (typeof isTargetedPath !== "boolean") {
		throw new Error(
			"isTargetedPath parameter not supplied to shouldFilteringModalRender helper function or is not a boolean",
		);
	}
	if (typeof hasModalBeenClosed !== "boolean") {
		throw new Error(
			"hasModalBeenClosed parameter not supplied to shouldFilteringModalRender helper function or is not a boolean",
		);
	}
	if (typeof isModalUrlParamMatched !== "boolean") {
		throw new Error(
			"isModalUrlParamMatched parameter not supplied to shouldFilteringModalRender helper function or is not a boolean",
		);
	}
	if (!vertical) {
		throw new Error(
			"vertical parameter not supplied to shouldFilteringModalRender helper function",
		);
	}
	/*
	 * Since we're using the site and vertical specific configurations to determine if the filtering modal should be rendered and
	 * and it's currently disabled across all sites and verticals, we need a way to force render it for Storybook and Chromatic. Hence,
	 * returning true if isForceRendered === true
	 * */
	if (isForceRendered && !hasModalBeenClosed) return true;
	if (isTargetedPath) {
		/* Check if it should be rendered according to the site and vertical specific config when the modal url param is matched*/
		return (
			pathOr(
				false,
				[vertical, "isTargetedPathModalFilteringAllowed"],
				constants.SITE_INFORMATION,
			) &&
			isModalUrlParamMatched &&
			!hasModalBeenClosed
		);
	} else {
		/* On non-targeted where matching the modal url param doesn't apply, rendering is purely predicated on the vertical configuration */
		return (
			pathOr(
				false,
				[vertical, "isNonTargetedPathModalFilteringAllowed"],
				constants.SITE_INFORMATION,
			) && !hasModalBeenClosed
		);
	}
};

export const resolveMainBenefitsField = (
	vertical,
	urlPathFilter,
	isMobileTargetedPath,
	filteringInformation,
	company,
) => {
	const { creditQuality, isNotModalFiltered, isNotUserFiltered } =
		filteringInformation;
	const isFilteringApplied = !isNotModalFiltered || !isNotUserFiltered;
	/* Function to make this more extensible/using different UI filters to dynamically change Company Main Benefits fields */
	const returnUiFilteringCompanyBenefitsFieldPath = (uiFilter) => [
		constants.SITE,
		vertical,
		constants.CUSTOM_MAIN_BENEFITS_FIELD_BY_UI_FILTERING,
		uiFilter,
	];
	const uiFilteredCompanyBenefitsFieldPath =
		returnUiFilteringCompanyBenefitsFieldPath(creditQuality);

	const urlPathFilteredCompanyBenefitsFieldPath = [
		constants.SITE,
		vertical,
		urlPathFilter,
		constants.CUSTOM_MAIN_BENEFITS_FIELD,
	];

	const mobileCompanyMainBenefitsField = [
		constants.SITE,
		vertical,
		constants.CUSTOM_MAIN_BENEFITS_FIELD,
	];

	const isMobileFieldDefinedInConfig = !!path(
		mobileCompanyMainBenefitsField,
		customBehaviorByMobileTargetedPath,
	);
	const isMobileFieldPresentInCompany = !!path(
		["fields", "mobileCompanyMainBenefits"],
		company,
	);

	// Use mobile benefits when applicable even when there's a url path filer e.g. mobileCompanyMainBenefits
	const shouldUseMobileBenefits =
		isMobileTargetedPath &&
		isMobileFieldDefinedInConfig &&
		isMobileFieldPresentInCompany;

	/*
  Fallback logic to use targeted slug for benefits field path when mobile can't be used (config doesn't exit or company doesn't have 'mobileCompanyMainBenefits')

  This logic means that mobileCompanyMainBenefits takes precedence over a custom field based on targeted slugs when applicable.

  An example would be `/personal-loans/a/bad-credit` where a company that has a mobileCompanyMainBenefits field with data will be used,
  otherwise it falls back to using the 'poorCreditCompanyMainBenefits'
  */
	const benefitsFieldPathByUrl = shouldUseMobileBenefits
		? mobileCompanyMainBenefitsField
		: urlPathFilteredCompanyBenefitsFieldPath;

	const customBehaviorObject = shouldUseMobileBenefits
		? customBehaviorByMobileTargetedPath
		: customBehaviorByPathsWithFilter;

	const benefitsFieldPath = isFilteringApplied
		? uiFilteredCompanyBenefitsFieldPath
		: benefitsFieldPathByUrl;
	return pathOr("companyMainBenefits", benefitsFieldPath, customBehaviorObject);
};

export const getFallbackCompanies = (
	contentfulCompanies,
	vertical,
	sentryExceptionState,
) => {
	if (!vertical)
		throw new Error(
			"vertical not passed to ensureNonZeroCompanyCount helper function",
		);
	if (!contentfulCompanies)
		throw new Error(
			`No Contentful companies associated with vertical: ${vertical}. Every vertical needs at least 1 company`,
		);

	const fallbackCompaniesFilter = pipe(
		filterEligibleAndGeoRelevantCompanies, // company must be recommended and permitted in user's geo
		filterAdNetworkCompanies, // filter out unmatched ad network companies
	);
	const fallbackCompanies = fallbackCompaniesFilter(contentfulCompanies);
	if (fallbackCompanies.length === 0) {
		throw new Error(
			`Unable to render fallback companies. No eligible fallback companies for vertical: ${vertical}. There must be a recommended company with an organic partnership for every vertical.`,
		);
	} else {
		logError(
			`Fallback companies rendered for vertical: ${vertical}. This may have occurred for various reasons (user applied filtering, no MA company matches in payload, etc...)`,
		);
		return fallbackCompanies;
	}
};
